import * as React from 'react';
import { Upload } from 'lucide-react';
import type { UploadedFontFace } from '@proptr/shared';
import { Button } from './ui/button.js';
import { Label } from './ui/label.js';
import { Select, type SelectOption } from './ui/select.js';
import { ensureGoogleFont } from '../lib/googleFont.js';

// Typeface picking, shared by the feed builder and the post builder.
//
// Both offer the same choice — a dropdown of hosted families, or the
// brokerage's own uploaded file — and both have to register an uploaded
// face with the FontFace API before the preview can render in it. Two
// copies of that would be two things to keep in step.

export const FONT_OPTIONS: SelectOption<string>[] = [
  // Sans
  { value: "'Jost', system-ui, sans-serif", label: 'Jost' },
  { value: "'Inter', system-ui, sans-serif", label: 'Inter' },
  { value: "'Archivo', system-ui, sans-serif", label: 'Archivo' },
  { value: "'Poppins', system-ui, sans-serif", label: 'Poppins' },
  { value: "'DM Sans', system-ui, sans-serif", label: 'DM Sans' },
  { value: "'Manrope', system-ui, sans-serif", label: 'Manrope' },
  { value: "'Outfit', system-ui, sans-serif", label: 'Outfit' },
  { value: "'Plus Jakarta Sans', system-ui, sans-serif", label: 'Plus Jakarta Sans' },
  { value: "'Figtree', system-ui, sans-serif", label: 'Figtree' },
  { value: "'Sora', system-ui, sans-serif", label: 'Sora' },
  { value: "'Urbanist', system-ui, sans-serif", label: 'Urbanist' },
  { value: "'Work Sans', system-ui, sans-serif", label: 'Work Sans' },
  { value: "'Space Grotesk', system-ui, sans-serif", label: 'Space Grotesk' },
  { value: "'Montserrat', system-ui, sans-serif", label: 'Montserrat' },
  { value: "'Raleway', system-ui, sans-serif", label: 'Raleway' },
  { value: "'Rubik', system-ui, sans-serif", label: 'Rubik' },
  { value: "'Karla', system-ui, sans-serif", label: 'Karla' },
  { value: "'Lato', system-ui, sans-serif", label: 'Lato' },
  { value: "'Mulish', system-ui, sans-serif", label: 'Mulish' },
  { value: "'Nunito Sans', system-ui, sans-serif", label: 'Nunito Sans' },
  { value: "'Open Sans', system-ui, sans-serif", label: 'Open Sans' },
  { value: "'Roboto', system-ui, sans-serif", label: 'Roboto' },
  { value: "'Source Sans 3', system-ui, sans-serif", label: 'Source Sans 3' },
  { value: "'Barlow', system-ui, sans-serif", label: 'Barlow' },
  { value: "'Albert Sans', system-ui, sans-serif", label: 'Albert Sans' },
  // Serif — the usual choice for a premium listing lockup
  { value: "'Playfair Display', Georgia, serif", label: 'Playfair Display' },
  { value: "'Cormorant Garamond', Georgia, serif", label: 'Cormorant Garamond' },
  { value: "'DM Serif Display', Georgia, serif", label: 'DM Serif Display' },
  { value: "'Fraunces', Georgia, serif", label: 'Fraunces' },
  { value: "'Libre Baskerville', Georgia, serif", label: 'Libre Baskerville' },
  { value: "'Lora', Georgia, serif", label: 'Lora' },
  { value: "'Merriweather', Georgia, serif", label: 'Merriweather' },
  { value: "'EB Garamond', Georgia, serif", label: 'EB Garamond' },
  { value: 'Georgia, serif', label: 'Georgia (system)' },
  // Mono
  { value: "'Geist Mono', ui-monospace, monospace", label: 'Geist Mono' },
  { value: "'JetBrains Mono', ui-monospace, monospace", label: 'JetBrains Mono' },
  { value: "'IBM Plex Mono', ui-monospace, monospace", label: 'IBM Plex Mono' },
  { value: "'Space Mono', ui-monospace, monospace", label: 'Space Mono' },
  { value: "'Roboto Mono', ui-monospace, monospace", label: 'Roboto Mono' },
];

// Font picker with an escape hatch: choose one of ours, or upload a
// licensed typeface. Once a file is uploaded it wins over the dropdown,
// and clearing it hands control back.
export function FontField({
  id,
  label,
  value,
  face,
  onChange,
  onUpload,
  onClear,
}: {
  id: string;
  label: string;
  value: string;
  face: UploadedFontFace | null;
  onChange: (v: string) => void;
  onUpload: (file: File) => Promise<void>;
  onClear: () => void;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Pull the family down as soon as it's chosen so the preview renders in
  // it rather than falling back until some later paint.
  React.useEffect(() => {
    if (!face) ensureGoogleFont(value);
  }, [value, face]);

  const pick = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      await onUpload(file);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      {face ? (
        <div className="mt-1 flex h-11 items-center gap-2 rounded-md border border-border bg-muted/30 px-3">
          {/* The stored family is a generated id, not something to show a
              user — the uploaded file's name is the recognisable part. */}
          <span
            className="flex-1 truncate font-mono text-[12px] text-foreground/80"
            style={{ fontFamily: `'${face.family}', inherit` }}
            title={face.url}
          >
            {fileLabelFor(face.url)}
          </span>
          <button
            type="button"
            onClick={onClear}
            className="shrink-0 font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground transition-colors hover:text-destructive"
          >
            Remove
          </button>
        </div>
      ) : (
        <div className="mt-1 flex items-center gap-2">
          <Select
            className="flex-1 bg-muted/30"
            options={FONT_OPTIONS}
            value={value}
            onChange={onChange}
            searchPlaceholder="Search fonts…"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-11 shrink-0"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            <Upload />
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".woff,.woff2,.ttf,.otf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void pick(f);
          // Reset so picking the same file twice still fires onChange.
          e.target.value = '';
        }}
      />
      <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
        {error ? (
          <span className="text-destructive">{error}</span>
        ) : face ? (
          'Custom font — replaces the dropdown selection.'
        ) : (
          'Pick one of ours, or upload a .woff2 / .ttf / .otf.'
        )}
      </p>
    </div>
  );
}

/** Best-effort display name for an uploaded face: the object key's leaf,
 *  which the upload route stamps as `font-<ts>-<slug>.woff2`. */
function fileLabelFor(url: string): string {
  try {
    const leaf = new URL(url).pathname.split('/').pop();
    return leaf ? decodeURIComponent(leaf) : 'Custom font';
  } catch {
    return 'Custom font';
  }
}

/**
 * Registers uploaded faces with the document so the live preview renders
 * in them — the same job RenderPage does for the headless export, which
 * is why both go through generated family names rather than the filename.
 */
export function useUploadedFonts(faces: (UploadedFontFace | null)[]): void {
  // Serialised so the effect re-runs on a genuine change, not on every
  // render's fresh array identity.
  const key = JSON.stringify(
    faces.filter(Boolean).map((f) => [f!.family, f!.url]),
  );
  React.useEffect(() => {
    let cancelled = false;
    const added: FontFace[] = [];
    void Promise.all(
      (JSON.parse(key) as [string, string][]).map(async ([family, url]) => {
        // Already registered (e.g. re-render after an unrelated edit).
        for (const f of document.fonts) if (f.family === family) return;
        try {
          const face = new FontFace(family, `url(${url})`);
          await face.load();
          if (cancelled) return;
          document.fonts.add(face);
          added.push(face);
        } catch {
          // A face that won't load just falls back in the preview.
        }
      }),
    );
    return () => {
      cancelled = true;
      for (const f of added) document.fonts.delete(f);
    };
  }, [key]);
}

/**
 * Generated family name for an uploaded face.
 *
 * An unsaved record has no id yet, so it borrows a per-session random
 * token. That token then sticks for the life of the face: it is already
 * unique, and rewriting it to the real id after the first save would
 * change the brand kit, hence the render hash, hence re-render every
 * creative — churn for a cosmetically nicer name.
 *
 * `scope` keeps the two builders' families apart.
 */
export function brandFontFamily(
  scope: string,
  id: string | null,
  sessionToken: string,
  slot: 'display' | 'mono',
): string {
  return `${scope}-${id ?? sessionToken}-${slot}`;
}
